"use client";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { useSignIn } from "@clerk/nextjs/legacy";
import { ClerkProviderBoundary } from "~/components/auth/ClerkProviderBoundary";
import { EmailCodeSignIn } from "~/components/auth/EmailCodeSignIn";
import { SignInStepFrame } from "~/components/auth/SignInStepFrame";
import { GoogleSignInButton } from "~/components/auth/GoogleSignInButton";
import { useClerkTicketSession } from "~/components/auth/clerkTicketSession";
import { TwtBrand } from "~/components/TwtBrand";
import { SignInModalStyles } from "~/components/auth/SignInModalStyles";
import { SignInTermsLine } from "~/components/auth/SignInTermsLine";
import { wallSerif as releaseSerif } from "~/styles/fonts/signInWall";
import {
  ButtonSpinner,
  DiscordIcon,
  PasskeyIcon,
} from "~/components/auth/signInModalIcons";
import {
  beginCodeFlow,
  clerkEmailSeed,
  clerkOauthRedirectUrls,
  codeFlowIsCurrent,
  completeOutcome,
  createCodeFlowState,
  endCodeFlow,
  type CompleteResponse,
} from "./signInPanelFlow";
type Props = {
  nextPath?: string;
  /** A full address validated by the newsletter server. */
  initialEmail?: string;
  variant?: "default" | "newsletter";
  /** A masked remembered address never fills the email field. */
  initialRememberedEmail?: string;
  initialError?: string;
  initialCodeStep?: boolean;
  initialMaskedEmail?: string;
  consumeStoredEmail?: boolean;
  openEmailField?: boolean;
  forceEmailCode?: boolean;
  /**
   * The wall's own title changes with the email step ("Enter your email",
   * "Enter code"), so the panel reports the step it is on.
   */
  onCodeStepChange?: (codeStep: boolean) => void;
  /** Checkout continues in place after the TWT session has been created. */
  onSignedIn?: () => void | Promise<void>;
};

type Mode = "email" | "complete";
type OauthMethod = "oauth_google" | "oauth_discord";
const SIGN_IN_PROVIDER_ORDER = [
  "oauth_google",
  "passkey",
  "oauth_discord",
] as const;
type Busy = "" | "passkey" | "oauth" | "complete";
type Handle = { generation: number };
type Completion = { sessionId: string; active: boolean };

export function TwtSignInPanel({
  nextPath = "/dashboard",
  initialEmail = "",
  variant = "default",
  initialRememberedEmail = "",
  initialError = "",
  onCodeStepChange,
  onSignedIn,
}: Props) {
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const clerk = useClerkTicketSession();
  const [mode, setMode] = useState<Mode>("email");
  const [busy, setBusy] = useState<Busy>("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [oauthMethod, setOauthMethod] = useState<OauthMethod | "">("");
  const [error, setError] = useState("");
  const [emailCodeStep, setEmailCodeStep] = useState(false);
  const [completionRetry, setCompletionRetry] = useState(true);
  const stepDirection = useRef<"forward" | "back">("forward");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const busyRef = useRef<Busy>("");
  const mountedRef = useRef(true);
  const codeFlowStateRef = useRef(createCodeFlowState());
  const completionRef = useRef<Completion | null>(null);
  const completeAbortRef = useRef<AbortController | null>(null);
  const id = useId();
  const safeNext = nextPath || "/dashboard";
  const newsletter = variant === "newsletter";
  useEffect(() => {
    const flowState = codeFlowStateRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      endCodeFlow(flowState);
      completeAbortRef.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (mode === "complete") headingRef.current?.focus();
  }, [mode, error]);
  const handleEmailCodeStepChange = useCallback(
    (codeStep: boolean) => {
      stepDirection.current = codeStep ? "forward" : "back";
      setEmailCodeStep(codeStep);
      onCodeStepChange?.(codeStep);
    },
    [onCodeStepChange]
  );
  function current(handle: Handle) {
    return (
      mountedRef.current && codeFlowIsCurrent(codeFlowStateRef.current, handle)
    );
  }
  function beginOperation(value: Busy): Handle | null {
    if (busyRef.current || emailBusy || !mountedRef.current) return null;
    busyRef.current = value;
    setBusy(value);
    setError("");
    return beginCodeFlow(codeFlowStateRef.current, "clerk");
  }
  function finishOperation(handle: Handle) {
    if (!current(handle)) return;
    busyRef.current = "";
    setBusy("");
  }
  async function finishSignIn(completion: Completion, handle: Handle) {
    if (!current(handle)) return;
    busyRef.current = "complete";
    setBusy("complete");
    stepDirection.current = "forward";
    setMode("complete");
    completionRef.current = completion;
    setCompletionRetry(true);
    try {
      if (!completion.active) {
        if (!setActive) throw new Error("Sign-in is still loading");
        await setActive({ session: completion.sessionId });
        if (!current(handle)) return;
        completion.active = true;
      }
      const controller = new AbortController();
      completeAbortRef.current = controller;
      const res = await fetch("/api/login/clerk/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        signal: controller.signal,
        body: JSON.stringify({ method: "passkey", next: safeNext }),
      });
      const body = (await res
        .json()
        .catch(() => ({}))) as CompleteResponse["body"];
      if (!current(handle)) return;
      const outcome = completeOutcome({ status: res.status, body });
      if (outcome.kind === "navigate") {
        if (onSignedIn) {
          await onSignedIn();
          if (current(handle)) {
            completionRef.current = null;
            finishOperation(handle);
            setMode("email");
          }
        } else window.location.replace(outcome.href);
        return;
      }
      finishOperation(handle);
      setCompletionRetry(outcome.recoverable || res.status >= 500);
      setError(outcome.message);
    } catch {
      if (!current(handle)) return;
      finishOperation(handle);
      setError(
        "Your sign-in was accepted, but we couldn't finish connecting to TWT. Try again."
      );
    } finally {
      if (current(handle)) completeAbortRef.current = null;
    }
  }
  async function retryCompletion() {
    const completion = completionRef.current;
    if (!completion) return;
    const handle = beginOperation("complete");
    if (handle) await finishSignIn(completion, handle);
  }
  async function startOauth(method: OauthMethod) {
    if (!signInLoaded || !signIn) return;
    const handle = beginOperation("oauth");
    if (!handle) return;
    setOauthMethod(method);
    const urls = clerkOauthRedirectUrls({ method, nextPath: safeNext });
    try {
      await signIn.authenticateWithRedirect({ strategy: method, ...urls });
    } catch {
      if (!current(handle)) return;
      finishOperation(handle);
      setOauthMethod("");
      setError("We could not open that sign-in. Try another option.");
    }
  }
  async function startPasskey() {
    if (!signInLoaded || !signIn || !setActive) return;
    const handle = beginOperation("passkey");
    if (!handle) return;
    try {
      const result = await signIn.authenticateWithPasskey({
        flow: "discoverable",
      });
      if (!current(handle)) return;
      if (result.status !== "complete" || !result.createdSessionId) {
        finishOperation(handle);
        setError("Passkey sign-in did not finish. Try another option.");
        return;
      }
      await finishSignIn(
        { sessionId: result.createdSessionId, active: false },
        handle
      );
    } catch (err) {
      if (!current(handle)) return;
      finishOperation(handle);
      const dismissed =
        err instanceof DOMException &&
        (err.name === "NotAllowedError" || err.name === "AbortError");
      setError(
        dismissed ? "" : "Passkey sign-in is not available. Try another option."
      );
    }
  }
  function backToEmail() {
    if (busyRef.current) return;
    endCodeFlow(codeFlowStateRef.current);
    completionRef.current = null;
    setError("");
    stepDirection.current = "back";
    setMode("email");
    handleEmailCodeStepChange(false);
  }
  const Heading = newsletter ? "h1" : "h2";
  const errorMessage = error ? (
    <p id={`${id}-error`} className="twt-signin-error" role="alert">
      {error}
    </p>
  ) : null;
  const providersDisabled = Boolean(busy) || emailBusy || !signInLoaded;
  const googleBusy = busy === "oauth" && oauthMethod === "oauth_google";
  const discordBusy = busy === "oauth" && oauthMethod === "oauth_discord";
  const providerIcons = (
    <>
      <div className="twt-signin-divider" aria-hidden="true">
        <span>or</span>
      </div>
      <div
        className="twt-signin-providers twt-signin-provider-icons"
        role="group"
        aria-label="Other ways to sign in"
      >
        {SIGN_IN_PROVIDER_ORDER.map((method) => {
          if (method === "oauth_google") {
            return (
              <GoogleSignInButton
                key={method}
                iconOnly
                disabled={providersDisabled}
                busy={googleBusy}
                onClick={() => void startOauth(method)}
              />
            );
          }
          const passkey = method === "passkey";
          const methodBusy = passkey ? busy === "passkey" : discordBusy;
          return (
            <button
              key={method}
              type="button"
              className={`twt-signin-secondary twt-signin-icon-button${
                passkey ? "" : " twt-signin-discord"
              }`}
              aria-label={
                passkey ? "Sign in with a passkey" : "Continue with Discord"
              }
              disabled={providersDisabled}
              aria-busy={methodBusy || undefined}
              onClick={() =>
                void (passkey ? startPasskey() : startOauth(method))
              }
            >
              {methodBusy ? (
                <ButtonSpinner />
              ) : passkey ? (
                <PasskeyIcon size={26} className="twt-signin-button-icon" />
              ) : (
                <DiscordIcon size={30} className="twt-signin-button-icon" />
              )}
            </button>
          );
        })}
      </div>
    </>
  );

  return (
    <>
      <SignInStepFrame
        stepKey={`${mode}:${emailCodeStep ? "code" : "email"}`}
        direction={stepDirection.current}
        className={`twt-signin${newsletter ? " twt-signin-newsletter" : ""}`}
      >
        {mode === "complete" ? (
          <>
            <Heading
              ref={headingRef}
              tabIndex={-1}
              className="twt-signin-heading"
            >
              {busy ? "Finishing your sign-in" : "Finish signing in"}
            </Heading>
            {errorMessage}
            {completionRetry ? (
              <button
                type="button"
                className="twt-signin-primary"
                disabled={Boolean(busy)}
                onClick={() => void retryCompletion()}
              >
                {busy ? "Finishing sign-in…" : "Try again"}
              </button>
            ) : (
              <Link className="twt-signin-back" href="/contact-us">
                Contact support
              </Link>
            )}
            <button
              type="button"
              className="twt-signin-back"
              disabled={Boolean(busy)}
              onClick={backToEmail}
            >
              Back to sign-in options
            </button>
          </>
        ) : (
          <>
            <EmailCodeSignIn
              nextPath={safeNext}
              clerk={clerk}
              initialEmail={
                clerkEmailSeed(initialEmail) ||
                clerkEmailSeed(initialRememberedEmail)
              }
              initialError={initialError}
              onSignedIn={onSignedIn}
              onCodeStepChange={handleEmailCodeStepChange}
              onBusyChange={setEmailBusy}
              disabled={Boolean(busy)}
              animateSteps={false}
            />
            {errorMessage}
            {!emailCodeStep ? providerIcons : null}
          </>
        )}
        <p className="twt-signin-status" role="status" aria-atomic="true" />
      </SignInStepFrame>
      <style jsx global>{`
        .twt-signin {
          display: flex;
          width: 100%;
          max-width: 440px;
          flex-direction: column;
          gap: 12px;
        }
        .twt-signin-email,
        .twt-signin-providers {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .twt-signin-label,
        .twt-signin .whop-oauth-email-label {
          color: var(--twt-ink, #141413);
          font-size: 14px;
          font-weight: 550;
        }
        .twt-signin-email-field,
        .twt-signin-code-input {
          min-height: 48px;
          width: 100%;
          border: 1px solid var(--twt-hairline, #e8e6dc);
          border-radius: 10px;
          background: var(--twt-card, #fff);
          padding: 10px 14px;
          color: var(--twt-ink, #141413);
          font-size: 16px;
          outline: none;
          transition: border-color 150ms ease-out, box-shadow 150ms ease-out;
        }
        .twt-signin-email-field::placeholder {
          color: var(--twt-graphite, #6a6a62);
        }
        .twt-signin-code-input {
          letter-spacing: 0.35em;
          font-size: 24px;
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          text-align: center;
        }
        .twt-signin-email-field:focus,
        .twt-signin-code-input:focus {
          border-color: var(--twt-ink, #141413);
          box-shadow: 0 0 0 2px rgb(20 18 15 / 12%);
        }
        .twt-signin-primary,
        .twt-signin-secondary {
          display: inline-flex;
          min-height: 48px;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          border: 1px solid transparent;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.4;
          transition: background-color 150ms ease-out,
            border-color 150ms ease-out, color 150ms ease-out;
        }
        .twt-signin-primary {
          background: var(--twt-ink, #141413);
          color: #fff;
        }
        .twt-signin-primary:disabled {
          cursor: not-allowed;
          background: #d8d2c7;
          color: #5f5a52;
        }
        .twt-signin-secondary {
          border-color: var(--twt-hairline, #e8e6dc);
          background: var(--twt-card, #fff);
          color: var(--twt-ink, #141413);
        }
        .twt-signin-secondary:disabled,
        .twt-signin-back:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        .twt-signin-primary:focus-visible,
        .twt-signin-secondary:focus-visible,
        .twt-signin-back:focus-visible {
          outline: 2px solid var(--twt-ink, #141413);
          outline-offset: 3px;
        }
        .twt-signin-primary:active:not(:disabled) {
          background: #35322d;
        }
        .twt-signin-secondary:active:not(:disabled) {
          background: #eee8de;
        }
        .twt-signin-captcha:empty {
          display: none;
        }
        .twt-signin-captcha {
          align-self: center;
        }
        .twt-signin-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--twt-graphite, #6a6a62);
          font-size: 13px;
        }
        .twt-signin-divider::before,
        .twt-signin-divider::after {
          content: "";
          height: 1px;
          flex: 1;
          background: var(--twt-hairline, #e8e6dc);
        }
        .twt-signin-back {
          display: inline-flex;
          min-height: 44px;
          align-items: center;
          justify-content: center;
          align-self: center;
          border: 0;
          border-radius: 6px;
          background: none;
          padding: 8px 4px;
          color: var(--twt-graphite, #6a6a62);
          font-size: 14px;
          line-height: 1.4;
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: color 150ms ease-out;
        }
        .twt-signin-row {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 8px 16px;
        }
        .twt-signin-notice,
        .twt-signin-status {
          margin: 0;
          color: var(--twt-ink-soft, #2a2a26);
          font-size: 14px;
          line-height: 1.6;
          overflow-wrap: anywhere;
          text-wrap: pretty;
        }
        .twt-signin-status:empty {
          display: none;
        }
        .twt-signin-error {
          margin: 0;
          color: var(--twt-oxblood-deep, #8a2f1f);
          font-size: 14px;
          line-height: 1.5;
        }
        .twt-signin-newsletter {
          --twt-ink: #1f1f1d;
          --twt-ink-soft: #5f5a52;
          --twt-graphite: #5f5a52;
          --twt-hairline: #d8d2c7;
          --twt-card: #fffaf2;
          --twt-canvas: #f6f3ee;
          max-width: 600px;
          color: #1f1f1d;
          background: #f6f3ee;
          gap: 16px;
        }
        .twt-signin-heading {
          margin: 0 0 12px;
          color: #1f1f1d;
          font-size: 36px;
          font-weight: 600;
          line-height: 1.15;
          letter-spacing: -0.025em;
          text-wrap: balance;
          outline: none;
        }
        .twt-signin-newsletter .twt-signin-primary,
        .twt-signin-newsletter .twt-signin-secondary,
        .twt-signin-newsletter .twt-signin-email-field,
        .twt-signin-newsletter .twt-signin-code-input {
          border-radius: 6px;
        }
        .twt-signin-newsletter .twt-signin-primary {
          background: #14120f;
          color: #fffaf2;
        }
        .twt-signin-newsletter .twt-signin-primary:disabled {
          background: #d8d2c7;
          color: #5f5a52;
        }
        .twt-signin-newsletter .twt-signin-providers {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .twt-signin-newsletter .twt-signin-notice {
          font-size: 16px;
        }
        .twt-signin-newsletter .twt-signin-code-input {
          min-height: 60px;
        }
        .twt-signin-newsletter .twt-signin-email {
          gap: 12px;
        }
        .twt-signin-newsletter .twt-signin-label {
          margin-bottom: -4px;
        }
        .twt-signin-newsletter .twt-signin-email-field {
          min-height: 54px;
          padding: 14px 16px;
        }
        .twt-signin-newsletter .twt-signin-primary,
        .twt-signin-newsletter .twt-signin-secondary {
          min-height: 50px;
          padding: 12px 18px;
        }
        .twt-signin-newsletter .twt-signin-divider {
          margin-block: 4px;
        }
        .twt-signin-passkey {
          min-height: 44px;
          align-self: center;
          padding: 8px 16px;
          border: 0;
          border-radius: 6px;
          background: transparent;
          color: #5f5a52;
          font-size: 14px;
          transition: background-color 120ms ease-out, color 120ms ease-out;
        }
        .twt-signin-member-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 8px;
          padding-top: 16px;
          border-top: 1px solid #d8d2c7;
          color: #5f5a52;
          font-size: 14px;
        }
        .twt-signin-member-action {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          min-height: 44px;
          border: 0;
          border-radius: 6px;
          padding: 8px 12px;
          margin-right: -12px;
          background: transparent;
          color: #1f1f1d;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
          transition: background-color 120ms ease-out;
        }
        .twt-signin-member-action:focus-visible,
        .twt-signin-passkey:focus-visible {
          outline: 2px solid #14120f;
          outline-offset: 3px;
        }
        .twt-signin-member-action:disabled,
        .twt-signin-passkey:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        @media (hover: hover) {
          .twt-signin-member-action:hover:not(:disabled),
          .twt-signin-passkey:hover:not(:disabled) {
            background: #eee8de;
            color: #14120f;
          }
          .twt-signin-primary:hover:not(:disabled) {
            background: #35322d;
          }
          .twt-signin-secondary:hover:not(:disabled) {
            background: var(--twt-canvas, #faf9f5);
            border-color: var(--twt-graphite, #6a6a62);
          }
          .twt-signin-back:hover:not(:disabled) {
            color: var(--twt-ink, #141413);
          }
        }
        @media (max-width: 420px) {
          .twt-signin-heading {
            font-size: 30px;
          }
          .twt-signin-newsletter .twt-signin-providers {
            grid-template-columns: 1fr;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .twt-signin-email-field,
          .twt-signin-code-input,
          .twt-signin-primary,
          .twt-signin-secondary,
          .twt-signin-back,
          .twt-signin-passkey,
          .twt-signin-member-action {
            transition: none;
          }
        }
      `}</style>
    </>
  );
}

/** The shared email form remains visible when Clerk is unavailable. */
export function TwtSignInPanelWithoutClerk({
  initialEmail = "",
  initialRememberedEmail = "",
  onCodeStepChange,
  nextPath = "/dashboard",
}: Props) {
  return (
    <SignInStepFrame stepKey="email" className="twt-signin">
      <EmailCodeSignIn
        nextPath={nextPath}
        clerk={null}
        disabled
        initialEmail={
          clerkEmailSeed(initialEmail) || clerkEmailSeed(initialRememberedEmail)
        }
        initialError="Sign-in is not available right now. Try again shortly."
        onCodeStepChange={onCodeStepChange}
        animateSteps={false}
      />
    </SignInStepFrame>
  );
}
/** Each surface uses this panel and its page-scoped Clerk provider. */
export function TwtSignInPanelWithProvider({
  clerkEnabled = true,
  onCodeStepChange: reportCodeStep,
  ...props
}: Props & { clerkEnabled?: boolean }) {
  const [codeStep, setCodeStep] = useState(false);
  const titleId = useId();
  const onCodeStepChange = useCallback(
    (next: boolean) => {
      setCodeStep(next);
      reportCodeStep?.(next);
    },
    [reportCodeStep]
  );
  const panelProps = { ...props, onCodeStepChange };
  const withoutClerk = <TwtSignInPanelWithoutClerk {...panelProps} />;
  const panel = clerkEnabled ? (
    <ClerkProviderBoundary fallback={withoutClerk} prefetchUI={false}>
      <TwtSignInPanel {...panelProps} />
    </ClerkProviderBoundary>
  ) : (
    withoutClerk
  );
  if (props.variant === "newsletter") return panel;
  return (
    <section
      className={`twt-signin-modal twt-signin-screen ${releaseSerif.variable}`}
      aria-labelledby={titleId}
    >
      <SignInModalStyles />
      <div className="twt-signin-screen-head">
        <TwtBrand
          invertedMark
          className="twt-signin-brand"
          markClassName="twt-signin-brand-mark"
          textClassName="twt-signin-brand-word"
          emphasisClassName="twt-signin-brand-emphasis"
        />
        <h2 id={titleId} className="whop-login-title">
          {codeStep ? "Enter code" : "Enter your email"}
        </h2>
        {codeStep ? (
          <p className="whop-login-subtext">
            Check your email for a one-time passcode.
          </p>
        ) : null}
      </div>
      {panel}
      {codeStep ? null : <SignInTermsLine />}
    </section>
  );
}
